import { useState } from 'react';
import { LemonadeAvatar } from './LemonadeAvatar';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ForgotPasswordProps {
  onBack: () => void;
}

export function ForgotPassword({ onBack }: ForgotPasswordProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isValid = email.includes('@') && email.length >= 5;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;

    setError(null);
    setIsLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (resetError) {
        setError(resetError.message);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex-1 flex flex-col items-center justify-start pt-16 px-6">
        <LemonadeAvatar />

        <div className="w-full max-w-lg mt-8 text-center space-y-8">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-primary" />
          </div>

          <div className="space-y-2">
            <h1 className="text-4xl lg:text-5xl font-light text-foreground">
              Check your email
            </h1>
            <p className="text-lg text-muted-foreground">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
          </div>

          <p className="text-muted-foreground">
            Didn't receive the email? Check your spam folder or{' '}
            <button
              type="button"
              onClick={() => setSuccess(false)}
              className="text-primary hover:underline font-medium"
            >
              try again
            </button>
          </p>

          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-start pt-16 px-6">
      <LemonadeAvatar />

      <form onSubmit={handleSubmit} className="w-full max-w-lg mt-8 text-center space-y-8">
        <div className="space-y-2">
          <h1 className="text-4xl lg:text-5xl font-light text-foreground">
            Forgot password?
          </h1>
          <p className="text-lg text-muted-foreground">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <div className="space-y-4">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-4 rounded-lg border-2 border-border bg-background focus:border-primary focus:outline-none text-lg text-foreground placeholder-muted-foreground"
            autoFocus
          />

          {error && (
            <p className="text-base text-destructive">{error}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={!isValid || isLoading}
          className={`w-full py-4 rounded-lg font-medium text-lg tracking-wide transition-all flex items-center justify-center gap-2 ${
            isValid && !isLoading
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : (
            'SEND RESET LINK'
          )}
        </button>

        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>
      </form>
    </div>
  );
}
